import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import SearchField from '../common/SearchField';
import { useGetMasterPageMutation } from '../../api/master/MasterApi';
import { useGetEmiPageMutation, useMarkEmiSettledMutation } from '../../api/reports/ReportApi';
import { EmiReportRow } from '../../types/reports';
import { MasterRow } from '../../types/master';
import { exportToExcel } from '../../utils/exportExcel';
import { longDate, invoiceDate } from '../../utils/dates';
import { useSnackbar } from '../../utils/snackbar';
import { can } from '../../utils/auth';

const money = (value?: number | null) => Number(value || 0).toFixed(2);
const centred = { flex: 1, align: 'center', headerAlign: 'center' } as const;

/**
 * EMI sales and what each finance company still owes the shop.
 *
 * <p>The tick is the shop saying "their payout reached us" - until then the financed
 * amount counts as due from the bank. It asks before it acts: that tick moves money
 * off the books, and the payout rarely lands on the day somebody gets round to it.
 */
const EmiReport = () => {
  const { openSnackbar } = useSnackbar();
  const [searchValue, setSearchValue] = useState('');
  const [bankId, setBankId] = useState('');
  const [settled, setSettled] = useState('false');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [banks, setBanks] = useState<MasterRow[]>([]);

  const [rows, setRows] = useState<EmiReportRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [dueFromBanks, setDueFromBanks] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  /** The row waiting on a yes - ticking a payout is not an undoable click. */
  const [confirming, setConfirming] = useState<{ row: EmiReportRow; next: boolean } | null>(null);
  const [settledDate, setSettledDate] = useState(new Date().toISOString().slice(0, 10));

  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [getMasterPage] = useGetMasterPageMutation();
  const [getEmiPage] = useGetEmiPageMutation();
  const [markSettled] = useMarkEmiSettledMutation();

  const load = () => {
    if (timeout.current) clearTimeout(timeout.current);
    setLoading(true);
    timeout.current = setTimeout(async () => {
      try {
        const response = await getEmiPage({
          searchValue,
          bankId: bankId ? Number(bankId) : null,
          settled: settled === '' ? null : settled === 'true',
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }).unwrap();
        setRows(response?.data || []);
        setRowCount(response?.totalCount || 0);
        setDueFromBanks(Number(response?.dueFromBanks || 0));
      } catch {
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  useEffect(() => {
    load();
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, bankId, settled, fromDate, toDate, paginationModel.page, paginationModel.pageSize]);

  /**
   * Any change of filter puts the list back on the first page.
   *
   * <p>Page 4 of the old results is not page 4 of the new ones - usually it is past
   * the end of them, and the shop is shown an empty grid for a search that matched.
   */
  useEffect(() => {
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, bankId, settled, fromDate, toDate]);

  useEffect(() => {
    (async () => {
      try {
        const response = await getMasterPage({ path: 'emi-bank', searchValue: '', page: 0, size: 100 }).unwrap();
        setBanks(response?.data || []);
      } catch {
        setBanks([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const askFirst = (row: EmiReportRow, next: boolean) => {
    setSettledDate(new Date().toISOString().slice(0, 10));
    setConfirming({ row, next });
  };

  const confirmSettled = async () => {
    if (!confirming) return;
    try {
      await markSettled({ billId: confirming.row.billId, settled: confirming.next, settledDate }).unwrap();
      openSnackbar(
        confirming.next
          ? `${confirming.row.bankName} payout of ${money(confirming.row.financedAmount)} marked received`
          : 'Marked as not received',
        'success',
      );
      setConfirming(null);
      load();
    } catch (error: any) {
      openSnackbar(error?.data?.message || 'Could not update', 'error');
    }
  };

  const columns: GridColDef[] = [
    { field: 'billNo', headerName: 'Bill Number', ...centred, minWidth: 140, align: 'left', headerAlign: 'left' },
    {
      field: 'billDate',
      headerName: 'Date',
      ...centred,
      minWidth: 120,
      renderCell: (row) => invoiceDate(row.value as string),
    },
    {
      field: 'bankName',
      headerName: 'Bank',
      ...centred,
      minWidth: 150,
      renderCell: (row) => <Chip size="small" variant="outlined" label={String(row.value ?? '-')} />,
    },
    { field: 'customerName', headerName: 'Customer', ...centred, minWidth: 150, align: 'left', headerAlign: 'left' },
    { field: 'loanNumber', headerName: 'Loan No', ...centred, minWidth: 120 },
    {
      field: 'downPayment',
      headerName: 'Down (customer paid)',
      ...centred,
      minWidth: 100,
      renderCell: (row) => money(row.value as number),
    },
    {
      field: 'financedAmount',
      headerName: 'Financed (bank owes us)',
      ...centred,
      minWidth: 120,
      renderCell: (row) => (
        <Typography variant="body2" fontWeight={700}>
          {money(row.value as number)}
        </Typography>
      ),
    },
    {
      field: 'settled',
      headerName: 'Payout received?',
      ...centred,
      minWidth: 150,
      sortable: false,
      renderCell: (row) => (
        <Box textAlign="center">
          <Checkbox
            size="small"
            disabled={!can('REPORTS', 'edit')}
            checked={!!row.value}
            onChange={(event) => askFirst(row.row as EmiReportRow, event.target.checked)}
          />
          {row.row.settledDate && (
            <Typography variant="caption" color="text.secondary" display="block">
              {longDate(row.row.settledDate)}
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  const download = () =>
    exportToExcel(
      rows.map((row) => ({
        'Bill Number': row.billNo,
        Date: row.billDate,
        Bank: row.bankName,
        Customer: row.customerName,
        'Loan No': row.loanNumber,
        'Down Payment': row.downPayment,
        Financed: row.financedAmount,
        Total: row.billTotal,
        Settled: row.settled ? 'Yes' : 'No',
        'Settled On': row.settledDate,
      })),
      'emi-report',
      'EMI',
    );

  return (
    <Box>
      <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center" sx={{ mb: 2 }}>
        <SearchField value={searchValue} onChange={setSearchValue} placeholder="Any words: bill no, customer, mobile, loan no, bank..." width={280} />
        <TextField select label="Bank" size="small" sx={{ minWidth: 170 }} value={bankId} onChange={(event) => setBankId(event.target.value)}>
          <MenuItem value="">All banks</MenuItem>
          {banks.map((bank: any) => (
            <MenuItem key={bank.id} value={bank.id}>
              {bank.bankName}
            </MenuItem>
          ))}
        </TextField>
        <TextField select label="Payout" size="small" sx={{ width: 160 }} value={settled} onChange={(event) => setSettled(event.target.value)}>
          <MenuItem value="false">Not received</MenuItem>
          <MenuItem value="true">Received</MenuItem>
          <MenuItem value="">All</MenuItem>
        </TextField>
        <TextField label="From" size="small" type="date" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
        <TextField label="To" size="small" type="date" InputLabelProps={{ shrink: true }} value={toDate} onChange={(event) => setToDate(event.target.value)} />
        <Chip variant="outlined" label={`${rowCount} bill(s)`} />
        <Box flexGrow={1} />
        <Box textAlign="right">
          <Typography variant="caption" color="text.secondary">
            BANKS STILL OWE THE SHOP
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {money(dueFromBanks)}
          </Typography>
        </Box>
        <Button size="small" startIcon={<DownloadIcon />} onClick={download} disabled={rows.length === 0}>
          Excel
        </Button>
      </Stack>

      <DataGrid
        columns={columns}
        rows={rows}
        getRowId={(row) => row.billId}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={(model: GridPaginationModel) => setPaginationModel(model)}
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        loading={loading}
        rowHeight={58}
        sx={{ height: '65vh' }}
      />

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
        This report is money the <b>finance companies owe the shop</b> - not what the customer owes. The customer repays
        the bank directly, which never appears here. Tick a payout when the bank's transfer reaches the shop's account.
        What the shop owes its suppliers is on Purchase &rarr; Supplier Dues.
      </Typography>

      {/* Ticking a payout says money reached the shop's account. It is worth a
          question, and the date is worth asking for. */}
      <Dialog open={!!confirming} onClose={() => setConfirming(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">
            {confirming?.next ? 'Payout received?' : 'Mark as not received?'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {confirming?.row.billNo} &middot; {confirming?.row.bankName}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {confirming?.next ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Confirm that{' '}
                <Box component="span" fontWeight={700} color="text.primary">
                  {money(confirming?.row.financedAmount)}
                </Box>{' '}
                from {confirming?.row.bankName} has reached the shop. It stops counting as money due.
              </Typography>
              <TextField
                label="Received on"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={settledDate}
                onChange={(event) => setSettledDate(event.target.value)}
              />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              This puts {money(confirming?.row.financedAmount)} back on the list of money due from {confirming?.row.bankName}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" size="small" onClick={() => setConfirming(null)}>
            Cancel
          </Button>
          <Button variant="contained" size="small" color={confirming?.next ? 'primary' : 'warning'} onClick={confirmSettled}>
            {confirming?.next ? 'Yes, Received' : 'Yes, Undo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmiReport;
